<template>
  <div
    class="card mb-3 shadow-sm event-card"
    :class="{ 'dropdown-open': showDropdown }"
  >
    <div class="card-body p-2 position-relative">
      <!-- Cover image -->
      <RouterLink :to="routeTo" class="cover-link" :aria-label="`View ${title} album`">
        <div class="cover-350">
          <div class="cover-blur" :style="coverBgStyle" aria-hidden="true"></div>
          <div class="cover-gradient" aria-hidden="true"></div>
          <img
            class="cover-img"
            :src="photo"
            :alt="title"
            loading="lazy"
            @error="handleImageError"
          />
        </div>
      </RouterLink>

      <!-- Text content -->
      <div class="pt-3">
        <div class="d-flex justify-content-between align-items-start">
          <h5 class="mb-1 flex-grow-1 pe-2">{{ truncate(title) }}</h5>

          <!-- Custom Dropdown -->
          <div class="dropdown-wrapper" ref="dropdownRef">
            <button
              class="btn btn-light btn-sm p-2 rounded-circle shadow-sm"
              style="height:40px;width:40px; border:1px solid #fafafa;"
              @click.stop="toggleDropdown"
              @keydown.enter.space="toggleDropdown"
              @keydown.esc="closeDropdown"
              :aria-expanded="showDropdown.toString()"
              aria-haspopup="true"
              :aria-label="`Options for ${title}`"
              :aria-controls="`dropdown-${eventId}`"
            >
              <i class="fas fa-ellipsis-v text-secondary"></i>
            </button>

            <transition name="fade">
              <ul
                v-if="showDropdown"
                :id="`dropdown-${eventId}`"
                class="dropdown-menu-custom shadow-sm"
                role="menu"
                @click.stop
              > 

                    <!-- Only for my albums -->
                    <template v-if="isMine">
                      <li 
                        role="menuitem" 
                        tabindex="0"
                        @click="openViewPerms"
                      >
                        <i class="fas fa-eye text-primary"></i> View Permission
                      </li>
                      <li 
                        role="menuitem" 
                        tabindex="0"
                        @click="openUploadPerms"
                      >
                        <i class="fas fa-upload text-success"></i> Upload Permission
                      </li>
                      <li 
                        role="menuitem" 
                        tabindex="0"
                        @click="openShareAlbumFriends"
                      >
                        <i class="fas fa-share text-warning"></i> My Friends
                      </li>
                      <hr />
                    </template>

                    <!-- Always visible -->
                    <li 
                      role="menuitem" 
                      tabindex="0"
                      @click="openShareVia"
                    >
                      <i class="far fa-paper-plane text-info"></i> Share via…
                    </li>

              </ul>
            </transition>
          </div>
        </div>

        <p class="small mb-1 text-highlight">HOST: {{ host }}</p>

        <p class="small text-highlight mb-0 text-uppercase">
          <span class="emoji-icon mr-1" aria-hidden="true">📅</span>
          <span>{{ dateRange }}</span>
        </p>
      </div>
    </div>
  </div>

  <!-- ===== Modals (bound with v-model) ===== -->
  <AlbumViewPermissions
    v-model="showViewPerms"
    :event-id="eventId"
    :session-user-id="sessionUserId"
  />

  <UploadPermissionsModal
    v-model="showUploadPerms"
    :event-id="eventId"
    :session-user-id="sessionUserId"
  />

  <ShareAlbumModal
    v-model="showShareAlbum"
    :event-id="eventId"
    :session-user-id="sessionUserId"
    initial-tab="friends"
  />

  <ShareViaModal
    :visible="showShareVia"
    :title="title"
    :image="photo"
    :description="shareDescription"
    :dates="dateRange"
    :host="host"
    :location="shareLocation"
    :address="shareAddress"
    :map-url="shareMapUrl"
    :url="shareUrl"
    @close="showShareVia = false"
  />
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { ref, computed, onMounted, onBeforeUnmount } from "vue"
import { RouterLink } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { formatEventDateRange } from '@/utils/date'

// Modals
import AlbumViewPermissions from '@/components/common/AlbumViewPermissions.vue'
import UploadPermissionsModal from '@/components/common/UploadPermissionsModal.vue'
import ShareAlbumModal from '@/components/common/ShareAlbumModal.vue'
import ShareViaModal from '@/components/common/ShareViaModal.vue'

const props = defineProps({
  event: { 
    type: Object, 
    required: true,
    default: () => ({})
  },
  type: { 
    type: String, 
    default: "my",
    validator: (value) => ['my', 'shared', 'other'].includes(value)
  },
  sessionUserId: {
    type: String,
    default: ''
  }
})

/* ========= Session ========= */
const session = useSessionStore()
const sessionUserId = computed(() => 
  props.sessionUserId || String(session.userId ?? '')
)

/* ========= Basics ========= */
const isMine = computed(() => (props.type || 'my').toLowerCase() === 'my')

const title = computed(() => 
  props.event.orgname || props.event.eventname || props.event.title || "Untitled Event"
)

const host = computed(() => 
  props.event.hostname || props.event.host_name || props.event.host || "Unknown"
)

const photo = computed(() => {
  const e = props.event || {}
  const p = e.photo || e.eventphoto || e.cover_image || null
  return p && p !== "0" && p !== "null" && p !== "undefined"
    ? p
    : "https://dummyimage.com/1200x800/cccccc/ffffff&text=No+Image"
})
const coverBgStyle = computed(() => ({ backgroundImage: `url(${photo.value})` }))

const eventId = computed(() =>
  String(props.event?.resolved_id ?? props.event?.id ?? props.event?.eventid ?? '')
)

const routeTo = computed(() => {
  const id = eventId.value
  if (!id) return '/albums'
  
  const source = encodeURIComponent(props.type || "my")
  return `/album/${id}?source=${source}`
})

function truncate(str, maxLength = 60) {
  if (!str || typeof str !== 'string') return ""
  return str.length > maxLength ? str.slice(0, maxLength) + "…" : str
}

// Handle broken images
const handleImageError = (event) => {
  const target = event.target
  target.src = "https://dummyimage.com/1200x800/cccccc/ffffff&text=No+Image"
  target.alt = "Image not available"
}

/* ========= Share helpers ========= */
const dateRange = computed(() => {
  const e = props.event || {}
  const range = formatEventDateRange({
    fromDate: e.fromdate,
    toDate: e.todate,
    fromTime: e.fromtime,
    toTime: e.totime
  })
  return range || 'Date not specified'
})

const shareDescription = computed(() => {
  const e = props.event || {}
  return e.description || e.short_desc || `Check out ${title.value}`
})

const shareLocation = computed(() => props.event?.event_place || '')
const shareAddress = computed(() => {
  const e = props.event || {}
  const parts = [e.event_place, e.door_no, e.address].filter(Boolean)
  return parts.join(', ')
})
const shareMapUrl = computed(() => {
  const e = props.event || {}
  if (e.map_url) return e.map_url
  if (e.lat && e.lng) return `https://www.google.com/maps?q=${encodeURIComponent(e.lat)},${encodeURIComponent(e.lng)}`
  if (shareAddress.value) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shareAddress.value)}`
  return ''
})

const shareUrl = computed(() => {
  try {
    const origin = window?.location?.origin || ''
    return origin + routeTo.value
  } catch {
    return routeTo.value
  }
})

/* ========= Dropdown ========= */
const showDropdown = ref(false)
const dropdownRef = ref(null)
const albumDropdownBus = typeof window !== 'undefined' ? window : null
const closeAllHandler = () => { showDropdown.value = false }

function toggleDropdown() {
  if (!showDropdown.value && albumDropdownBus) {
    albumDropdownBus.dispatchEvent(new CustomEvent('album-dropdown-close-all'))
  }
  showDropdown.value = !showDropdown.value 
}

function closeDropdown() {
  showDropdown.value = false
}

function handleClickOutside(event) {
  if (showDropdown.value && dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showDropdown.value = false
  }
}

/* ========= Modal state + actions ========= */
const showViewPerms = ref(false)
const showUploadPerms = ref(false)
const showShareAlbum = ref(false)
const showShareVia = ref(false)

// Fixed modal opening functions
function openViewPerms() {
  if (!isMine.value) return
  console.log('Opening View Permissions modal') // Debug log
  showDropdown.value = false
  // Use setTimeout to ensure dropdown closes before modal opens
  setTimeout(() => {
    showViewPerms.value = true
  }, 10)
}

function openUploadPerms() {
  if (!isMine.value) return
  console.log('Opening Upload Permissions modal') // Debug log
  showDropdown.value = false
  setTimeout(() => {
    showUploadPerms.value = true
  }, 10)
}

function openShareAlbumFriends() {
  if (!isMine.value) return
  console.log('Opening Share Album modal') // Debug log
  showDropdown.value = false
  setTimeout(() => {
    showShareAlbum.value = true
  }, 10)
}

function openShareVia() {
  console.log('Opening Share Via modal') // Debug log
  showDropdown.value = false
  setTimeout(() => {
    showShareVia.value = true
  }, 10)
}

// Keyboard navigation for dropdown
function handleKeydown(event) {
  if (!showDropdown.value) return
  
  const menuItems = dropdownRef.value?.querySelectorAll('[role="menuitem"]')
  if (!menuItems?.length) return
  
  let currentIndex = -1
  for (let i = 0; i < menuItems.length; i++) {
    if (menuItems[i] === document.activeElement) {
      currentIndex = i
      break
    }
  }
  
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    const nextIndex = (currentIndex + 1) % menuItems.length
    menuItems[nextIndex]?.focus()
  } else if (event.key === 'ArrowUp') {
    event.preventDefault()
    const prevIndex = (currentIndex - 1 + menuItems.length) % menuItems.length
    menuItems[prevIndex]?.focus()
  } else if (event.key === 'Escape') {
    showDropdown.value = false
    dropdownRef.value?.querySelector('button')?.focus()
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside)
  document.addEventListener("keydown", handleKeydown)
  if (albumDropdownBus) {
    albumDropdownBus.addEventListener('album-dropdown-close-all', closeAllHandler)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside)
  document.removeEventListener("keydown", handleKeydown)
  if (albumDropdownBus) {
    albumDropdownBus.removeEventListener('album-dropdown-close-all', closeAllHandler)
  }
})
</script>

<style scoped>
.event-card {
  --border-width: 3px;
  --fill: 0deg;
  position: relative;
  border-radius: 20px;
  padding: 1.1rem;
  background: linear-gradient(135deg, #fff7ef 0%, #fff3e6 48%, #ffe9d7 100%);
  border: var(--border-width) solid transparent;
  box-shadow: 0 18px 36px rgba(249, 115, 22, 0.12);
  margin-bottom: 1.1rem;
  isolation: isolate;
  background-clip: padding-box;
  overflow: visible;
}
.event-card > * { position: relative; z-index: 1; }
.event-card::before {
  content: '';
  position: absolute;
  inset: calc(var(--border-width) * -1);
  z-index: 0;
  border-radius: inherit;
  background: conic-gradient(from -90deg, #ff7a1a 0deg, #ff9a3c var(--fill), transparent var(--fill), transparent 360deg);
  padding: var(--border-width);
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask-composite: exclude;
  opacity: 0;
  transition: opacity .25s ease, filter .25s ease;
  filter: drop-shadow(0 8px 18px rgba(249, 115, 22, 0.28));
}
.event-card:hover::before {
  opacity: 1;
  animation: fillBorderAlbum .9s ease;
  filter: drop-shadow(0 10px 22px rgba(249, 115, 22, 0.34));
}
.event-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 22px 48px rgba(249, 115, 22, 0.2);
}

@property --fill {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}

@keyframes fillBorderAlbum {
  from { --fill: 0deg; }
  to { --fill: 360deg; }
}

.card-body h5 { 
  font-size: 1.1rem; 
  line-height: 1.3;
}
.text-highlight{
  color:#b45309;
  font-weight:700;
}
.emoji-icon{
  display:inline-block;
  line-height:1;
}

/* fixed-height cover */
.cover-350 { 
  height: 350px; 
  overflow: hidden; 
  border-radius: 0.75rem;
  position: relative;
  background: #0f172a;
}
.cover-blur{
  position:absolute;
  inset:0;
  background-size:cover;
  background-position:center;
  filter:blur(28px) saturate(1.1);
  transform:scale(1.08);
  opacity:0;
  transition:opacity .2s ease;
  border-radius:inherit;
}
.cover-gradient{
  position:absolute;
  inset:0;
  background:linear-gradient(135deg, rgba(15,23,42,.75), rgba(2,6,23,.25));
  opacity:0;
  transition:opacity .2s ease;
  z-index:0;
  border-radius:inherit;
}
.cover-img{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
  position:relative;
  z-index:1;
  border-radius:0.75rem;
}
.cover-link { 
  display: block; 
  text-decoration: none; 
  color: inherit; 
}

/* Hover animation */
.event-card { 
  transition: transform 0.15s ease, box-shadow 0.15s ease; 
}
.event-card:not(.dropdown-open):hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

/* --- Custom dropdown --- */
.dropdown-wrapper { 
  position: relative; 
  z-index: 3;
}
.dropdown-wrapper button { 
  background: #fff; 
  border: none; 
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.dropdown-wrapper button:hover {
  background: #f8f9fa;
}

.dropdown-menu-custom {
  position: absolute; 
  right: 0; 
  top: 100%;
  min-width: 220px; 
  background: #fff; 
  border-radius: 8px;
  padding: 6px 0; 
  list-style: none; 
  z-index: 1010; 
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.event-card.dropdown-open {
  z-index: 4000;
}
.event-card.dropdown-open .dropdown-menu-custom {
  z-index: 4100;
}

.dropdown-menu-custom li {
  padding: 8px 14px; 
  font-size: 0.9rem; 
  cursor: pointer;
  display: flex; 
  align-items: center; 
  gap: 10px; 
  transition: background 0.2s;
  outline: none;
}
.dropdown-menu-custom li:hover,
.dropdown-menu-custom li:focus { 
  background: #f8f9fa; 
}

/* Divider */
.dropdown-menu-custom hr { 
  margin: 4px 0; 
  border-top: 1px solid #e9ecef; 
}

/* Fade animation */
.fade-enter-active, .fade-leave-active { 
  transition: opacity 0.15s ease, transform 0.15s ease; 
}
.fade-enter-from { 
  opacity: 0;
  transform: translateY(-8px);
}
.fade-leave-to { 
  opacity: 0;
  transform: translateY(-8px);
}

/* Focus styles for accessibility */
.dropdown-wrapper button:focus-visible,
.dropdown-menu-custom li:focus-visible {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .cover-350 {
    height: 320px;
  }
  .cover-blur,
  .cover-gradient{
    opacity:1;
  }
  .cover-img{
    object-fit:contain;
    padding:14px;
  }
}
@media (max-width: 768px) {
  .cover-350 {
    height: 260px;
  }
  .cover-img{
    padding:12px;
  }
  .dropdown-menu-custom {
    min-width: 200px;
    right: -8px;
  }
}
@media (max-width: 576px) {
  .cover-350 {
    height: 220px;
  }
  .cover-img{
    padding:10px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
.event-card,
.fade-enter-active,
.fade-leave-active {
  transition: none;
}
}

@keyframes rotateBorder {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

@supports not (background: conic-gradient(red, blue)) {
  .event-card::before {
    background-image: linear-gradient(90deg, #ff7a1a, #ff9a3c, #ffd166, #ff7a1a);
    animation: none;
  }
}
</style>
